// src/components/PuzzleSocket.tsx
import React, { useMemo, useRef } from "react";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";
import { useGLTF } from "@react-three/drei";
import {
  BG_Z,
  SCENE_FADE_IN_DURATION,
  HIGHLIGHT_STRENGTH,
  HIGHLIGHT_EXPONENT,
  HIGHLIGHT_LIGHT_DIR,
  SOCKET_GLOW_COLOR,
  SOCKET_EMISSIVE_BASE,
  SOCKET_EMISSIVE_PULSE,
  SOCKET_PULSE_SPEED,
} from "./puzzleConfig";

export function PuzzleSocket() {
  const { scene } = useGLTF("/models/BG.glb") as any;
  const emissiveMaterials = useRef<THREE.MeshStandardMaterial[]>([]);
  const pulseTime = useRef(0);
  const fadeInTime = useRef(0);

  const baseScene = useMemo(() => {
    const clone = scene.clone(true);
    emissiveMaterials.current = [];

    clone.traverse((obj: any) => {
      if (!obj.isMesh) return;

      const mesh = obj as THREE.Mesh;
      mesh.receiveShadow = true;

      const mat = mesh.material as THREE.Material | THREE.Material[] | undefined;

      const processMaterial = (material: THREE.Material) => {
        const std = material as THREE.MeshStandardMaterial & {
          userData?: any;
          onBeforeCompile?: (shader: any) => void;
        };

        if ("emissive" in std) {
          std.emissive = SOCKET_GLOW_COLOR.clone();
          std.emissiveIntensity = 0;
          emissiveMaterials.current.push(std);
        }

        const uData = std.userData || (std.userData = {});
        if (uData.__bgHighlightPatched) return;
        uData.__bgHighlightPatched = true;

        std.onBeforeCompile = (shader: any) => {
          shader.uniforms.uHighlightStrength = { value: HIGHLIGHT_STRENGTH };
          shader.uniforms.uHighlightExponent = { value: HIGHLIGHT_EXPONENT };
          shader.uniforms.uHighlightDir      = { value: HIGHLIGHT_LIGHT_DIR.clone() };

          shader.vertexShader =
            `varying vec3 vWorldNormal;\n` + shader.vertexShader;

          shader.vertexShader = shader.vertexShader.replace(
            "#include <beginnormal_vertex>",
            `#include <beginnormal_vertex>
  vWorldNormal = normalize( mat3( modelMatrix ) * objectNormal );`
          );

          shader.fragmentShader =
            `varying vec3 vWorldNormal;\nuniform vec3 uHighlightDir;\nuniform float uHighlightStrength;\nuniform float uHighlightExponent;\n` +
            shader.fragmentShader;

          shader.fragmentShader = shader.fragmentShader.replace(
            "#include <output_fragment>",
            `#include <output_fragment>
float ndl = max(dot(normalize(vWorldNormal), normalize(uHighlightDir)), 0.0);
float highlight = pow(ndl, uHighlightExponent) * uHighlightStrength;
gl_FragColor.rgb += highlight;`
          );
        };
      };

      if (Array.isArray(mat)) {
        mat.forEach(processMaterial);
      } else if (mat) {
        processMaterial(mat);
      }
    });

    return clone;
  }, [scene]);

  const groupRef = useRef<THREE.Group>(null);

  useFrame((_, delta) => {
    const g = groupRef.current;
    if (!g) return;

    pulseTime.current += delta;
    fadeInTime.current = Math.min(fadeInTime.current + delta, SCENE_FADE_IN_DURATION);
    const fadeIn = fadeInTime.current / SCENE_FADE_IN_DURATION;

    const wave = 0.5 + 0.5 * Math.sin(2.0 * Math.PI * SOCKET_PULSE_SPEED * pulseTime.current);
    const pulseIntensity = (SOCKET_EMISSIVE_BASE + SOCKET_EMISSIVE_PULSE * wave) * fadeIn;

    emissiveMaterials.current.forEach((mat) => {
      mat.emissiveIntensity = pulseIntensity;
      mat.emissive.copy(SOCKET_GLOW_COLOR);
    });
  });

  return (
    <group ref={groupRef} position={[0, 0, BG_Z]}>
      <primitive object={baseScene} />
    </group>
  );
}

useGLTF.preload("/models/BG.glb");
